/**
 * @file ScheduleOpsReport.tsx
 * @brief Company report — schedule bookings + reschedule outcomes.
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { CalendarDays, CalendarClock } from "lucide-react";
import {
  GridBand,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
} from "@/features/reports-shared/ReportSection";
import { ReportTable } from "./ReportTable";
import { useScheduleOpsReport } from "../api";
import { defaultReportRange } from "../defaultRange";
import { ReportShell } from "./ReportShell";

export function ScheduleOpsReportView() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultReportRange());
  const queryRange = useMemo(
    () => ({ startDate: range.startDate, endDate: range.endDate }),
    [range.startDate, range.endDate],
  );
  const report = useScheduleOpsReport(queryRange);
  const summary = report.data?.summary;
  const byStatus = Object.entries(summary?.by_status ?? {}).map(
    ([status, count]) => ({ status, count: Number(count) }),
  );
  const rescheduleRows = Object.entries(
    summary?.reschedule_by_status ?? {},
  ).map(([status, count]) => ({ status, count: Number(count) }));

  return (
    <ReportShell
      title="Schedule ops"
      description="Interpretation diary bookings and reschedule approve/decline rates."
      range={range}
      onRangeChange={setRange}
      exportDisabled={byStatus.length === 0 && rescheduleRows.length === 0}
      serverExport={{
        scope: "company",
        reportKey: "schedule-ops",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
        },
      }}
    >
      <>
        <ReportKpiRow className="xl:grid-cols-2">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Scheduled"
              icon={CalendarDays}
              value={String(summary?.total_scheduled ?? 0)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Approve rate"
              icon={CalendarClock}
              value={
                summary?.reschedule_approve_rate_pct == null
                  ? "—"
                  : `${summary.reschedule_approve_rate_pct}%`
              }
              hint="Of decided reschedule requests"
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <GridCells className="grid-cols-1 lg:grid-cols-2">
          <GridCell padded={false}>
            <GridBand>
              <h3 className="text-sm font-semibold tracking-tight">
                By schedule status
              </h3>
            </GridBand>
            <ReportTable
              headers={["Status", { label: "Count", align: "right" }]}
              isLoading={report.isLoading}
              empty={byStatus.length === 0}
            >
              {byStatus.map((row) => (
                <tr key={row.status} className="hover:bg-secondary/20">
                  <td className="px-4 py-2 capitalize font-medium">
                    {row.status}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {row.count}
                  </td>
                </tr>
              ))}
            </ReportTable>
          </GridCell>
          <GridCell padded={false}>
            <GridBand>
              <h3 className="text-sm font-semibold tracking-tight">
                Reschedule requests
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Created in the selected range.
              </p>
            </GridBand>
            <ReportTable
              headers={["Status", { label: "Count", align: "right" }]}
              isLoading={report.isLoading}
              empty={rescheduleRows.length === 0}
              emptyMessage="No reschedule requests in range."
            >
              {rescheduleRows.map((row) => (
                <tr key={row.status} className="hover:bg-secondary/20">
                  <td className="px-4 py-2 capitalize font-medium">
                    {row.status}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {row.count}
                  </td>
                </tr>
              ))}
            </ReportTable>
          </GridCell>
        </GridCells>
      </>
    </ReportShell>
  );
}
